/**
 * 世界模拟器AI服务
 * 负责整合世界模板的核心指令集，构建完整的AI提示词
 */

class WorldAIService {
  /**
   * 构建完整的AI系统提示词
   * @param {object} world - 世界数据
   * @param {object} session - 会话数据
   * @param {object} settings - 用户设置（可选）
   * @returns {string} 完整的系统提示词
   */
  buildSystemPrompt(world, session = null, settings = null) {
    const parts = []

    // 1. 世界观前置文本
    if (world.preText) {
      parts.push(world.preText)
      parts.push('\n---\n')
    }

    // 2. 知识库上下文（根据当前对话提取相关条目）
    if (world.worldBook && world.worldBook.length > 0) {
      const worldBookContext = this.buildWorldBookContext(world.worldBook, session)
      if (worldBookContext) {
        parts.push('# 知识库')
        parts.push(worldBookContext)
        parts.push('\n---\n')
      }
    }

    // 3. 核心AI指令集（完整保留，包括NSFW内容）
    if (world.postText) {
      let postText = world.postText
      
      // 如果设置了NSFW模式，确保NSFW内容被启用
      if (settings && settings.nsfwEnabled) {
        // 如果postText中没有NSFW相关指令，添加一个
        if (!postText.toLowerCase().includes('nsfw') && !postText.toLowerCase().includes('成人')) {
          postText += '\n\n[NSFW模式已启用：允许生成成人向内容]'
        }
      } else {
        // 如果NSFW未启用，移除或注释NSFW相关指令
        postText = postText.replace(/\[NSFW[^\]]*\]/gi, '[NSFW模式未启用]')
      }
      
      parts.push(postText)
      parts.push('\n---\n')
    }

    // 4. 输出格式规范
    if (world.prePrompt) {
      parts.push(world.prePrompt)
    }

    return parts.join('\n')
  }

  /**
   * 构建知识库上下文
   * @param {array} worldBook - 知识库数组
   * @param {object} session - 会话数据
   * @returns {string} 知识库上下文
   */
  buildWorldBookContext(worldBook, session) {
    if (!worldBook || worldBook.length === 0) return ''

    // 如果有会话，根据对话历史提取相关条目
    let relevantEntries = worldBook

    if (session && session.conversationHistory && session.conversationHistory.length > 0) {
      const recentMessages = session.conversationHistory.slice(-5) // 最近5条消息
      const recentText = recentMessages.map(m => m.content).join(' ')
      
      // 查找包含关键词的条目
      relevantEntries = worldBook.filter(entry => {
        const keywords = this.extractKeywords(entry.key)
        return keywords.some(keyword => recentText.includes(keyword))
      })

      // 如果没有匹配的，返回所有条目（限制数量）
      if (relevantEntries.length === 0) {
        relevantEntries = worldBook.slice(0, 20) // 最多20条
      }
    } else {
      // 初始状态，返回前20条
      relevantEntries = worldBook.slice(0, 20)
    }

    // 按分组组织
    const groupedEntries = {}
    relevantEntries.forEach(entry => {
      const group = entry.group || '其他'
      if (!groupedEntries[group]) {
        groupedEntries[group] = []
      }
      groupedEntries[group].push(entry)
    })

    // 生成知识库文本
    const lines = []
    for (const [group, entries] of Object.entries(groupedEntries)) {
      lines.push(`\n## ${group}`)
      entries.forEach(entry => {
        const keywords = this.extractKeywords(entry.key)
        lines.push(`\n**${keywords.join('、')}**`)
        lines.push(entry.value)
      })
    }

    return lines.join('\n')
  }

  /**
   * 从key字符串中提取关键词
   * @param {string} key - key字符串，可能包含_or_分隔的别名
   * @returns {array} 关键词数组
   */
  extractKeywords(key) {
    if (!key) return []
    
    // 移除 _or_ 前缀和 @wb@ 标记
    const cleaned = key.replace(/^_or_/, '').replace(/@wb@/g, ',')
    
    // 分割关键词
    return cleaned.split(',').map(k => k.trim()).filter(k => k)
  }

  /**
   * 构建用户消息
   * @param {string} userInput - 用户输入
   * @param {object} session - 会话数据
   * @returns {object} 包含role和content的消息对象
   */
  buildUserMessage(userInput, session = null) {
    return {
      role: 'user',
      content: userInput
    }
  }

  /**
   * 解析AI响应
   * @param {string} response - AI原始响应
   * @param {object} world - 世界数据
   * @returns {object} 解析后的结构化数据
   */
  parseAIResponse(response, world) {
    try {
      // 如果世界有prePrompt定义了特定格式，按格式解析
      if (world.prePrompt && world.prePrompt.includes('<!--')) {
        return this.parseStructuredResponse(response)
      }

      // 否则返回原始响应
      return {
        raw: response,
        paragraph: response,
        options: this.extractOptions(response),
        metadata: {}
      }
    } catch (error) {
      console.error('解析AI响应失败:', error)
      return {
        raw: response,
        paragraph: response,
        options: [],
        metadata: {}
      }
    }
  }

  /**
   * 解析结构化响应（按照pre_prompt格式）
   * @param {string} response - AI响应
   * @returns {object} 结构化数据
   */
  parseStructuredResponse(response) {
    const result = {
      raw: response,
      paragraph: '',
      options: [],
      metadata: {},
      characterInfo: {},
      worldInfo: {},
      memory: []
    }

    // 提取时间和地点信息（<!--1--> 区块）
    const timeMatch = response.match(/```Time\s+([\s\S]*?)```/i)
    if (timeMatch) {
      result.metadata.time = this.parseTimeBlock(timeMatch[1])
    }

    // 提取主要叙述内容（<!--2--> 区块）
    const narrativeMatch = response.match(/```Time[\s\S]*?```\s+([\s\S]*?)(?=```python|$)/i)
    if (narrativeMatch) {
      result.paragraph = narrativeMatch[1].trim()
    }

    // 提取主角信息（<!--3--> 区块）
    const protagonistMatch = response.match(/```python\s+# 主角\s+([\s\S]*?)```/i)
    if (protagonistMatch) {
      result.characterInfo.protagonist = this.parsePythonBlock(protagonistMatch[1])
    }

    // 提取其他角色信息（<!--4--> 区块）
    const npcMatches = response.matchAll(/```python\s+# 状态\s+([\s\S]*?)```/gi)
    result.characterInfo.npcs = []
    for (const match of npcMatches) {
      result.characterInfo.npcs.push(this.parsePythonBlock(match[1]))
    }

    // 提取世界信息（<!--5--> 区块）
    const worldMatch = response.match(/```python\s+#(?:大明王朝|世界信息)\s+([\s\S]*?)```/i)
    if (worldMatch) {
      result.worldInfo = this.parsePythonBlock(worldMatch[1])
    }

    // 提取记忆（<!--6--> 区块）
    const memoryMatch = response.match(/```python\s+# 记忆总结\s+([\s\S]*?)```/i)
    if (memoryMatch) {
      result.memory = this.parseMemoryBlock(memoryMatch[1])
    }

    // 提取选项（<!--7--> 区块）
    const optionsMatch = response.match(/```python\s+选项：\s+([\s\S]*?)```/i)
    if (optionsMatch) {
      result.options = this.parseOptionsBlock(optionsMatch[1])
    }

    return result
  }

  /**
   * 解析时间区块
   */
  parseTimeBlock(block) {
    const lines = block.split('\n')
    const info = {}
    
    lines.forEach(line => {
      const match = line.match(/^(.+?)：(.+)$/)
      if (match) {
        const key = match[1].replace(/^[📅🌏🏘️📖]/, '').trim()
        info[key] = match[2].trim()
      }
    })
    
    return info
  }

  /**
   * 解析Python代码块格式的数据
   */
  parsePythonBlock(block) {
    const data = {}
    const lines = block.split('\n')
    
    lines.forEach(line => {
      const match = line.match(/^(.+?)[:：](.+)$/)
      if (match) {
        const key = match[1].trim()
        let value = match[2].trim()
        
        // 移除引号
        value = value.replace(/^['"「『]|['"」』]$/g, '')
        
        data[key] = value
      }
    })
    
    return data
  }

  /**
   * 解析记忆区块
   */
  parseMemoryBlock(block) {
    const memories = []
    const sections = block.split(/(?=重要角色记忆|世界记忆)/g)
    
    sections.forEach(section => {
      const items = section.match(/[一二三四五六七八九十]+、(.+?)(?=\n[一二三四五六七八九十]+、|\n—+|$)/gs)
      if (items) {
        items.forEach(item => {
          const cleaned = item.replace(/^[一二三四五六七八九十]+、/, '').trim()
          if (cleaned) {
            memories.push(cleaned)
          }
        })
      }
    })
    
    return memories
  }

  /**
   * 解析选项区块
   */
  parseOptionsBlock(block) {
    const options = []
    const lines = block.split('\n')
    
    lines.forEach(line => {
      const match = line.match(/^([A-D])\.\s*(.+)$/)
      if (match) {
        options.push({
          id: match[1],
          text: match[2].trim()
        })
      }
    })
    
    return options
  }

  /**
   * 从文本中提取选项（简单版本）
   */
  extractOptions(text) {
    const options = []
    
    // 匹配 A. B. C. 格式
    const matches = text.matchAll(/([A-D])\.\s*([^\n]+)/g)
    for (const match of matches) {
      options.push({
        id: match[1],
        text: match[2].trim()
      })
    }
    
    return options
  }

  /**
   * 构建对话历史
   * @param {array} history - 对话历史数组
   * @param {number} maxMessages - 最大消息数
   * @returns {array} 格式化的对话历史
   */
  buildConversationHistory(history, maxMessages = 20) {
    if (!history || history.length === 0) return []
    
    // 只保留最近的消息
    const recentHistory = history.slice(-maxMessages)
    
    return recentHistory.map(msg => ({
      role: msg.role || (msg.isUser ? 'user' : 'assistant'),
      content: msg.content
    }))
  }

  /**
   * 生成对话记忆摘要
   * @param {array} conversationHistory - 对话历史
   * @param {object} world - 世界数据
   * @param {number} summaryInterval - 摘要间隔（消息数）
   * @returns {Promise<string>} 记忆摘要
   */
  async generateMemorySummary(conversationHistory, world, summaryInterval = 10) {
    if (!conversationHistory || conversationHistory.length < summaryInterval) {
      return ''
    }

    try {
      // 获取需要总结的对话片段
      const messagesToSummarize = conversationHistory.slice(-summaryInterval)
      const conversationText = messagesToSummarize
        .map(msg => `${msg.role === 'user' ? '玩家' : '世界'}: ${msg.content}`)
        .join('\n\n')

      const summaryPrompt = `请总结以下对话中的关键信息，包括：
1. 玩家角色的重要行动和决定
2. 发生的重要事件和情节进展
3. 角色关系的变化
4. 世界状态的重要变化
5. 需要记住的关键细节

对话内容：
${conversationText}

世界设定：${world.name || '未知世界'}

请用简洁的语言总结（100-200字），只保留最重要的信息。`

      const { RealAIService } = await import('./realAiService.js')
      const summary = await RealAIService.callLanguageModel(summaryPrompt, {
        temperature: 0.3,
        maxTokens: 300
      })

      return summary.trim()
    } catch (error) {
      console.error('生成记忆摘要失败:', error)
      return ''
    }
  }

  /**
   * 构建包含记忆的系统提示词
   * @param {object} world - 世界数据
   * @param {object} session - 会话数据
   * @param {object} settings - 用户设置
   * @param {array} conversationHistory - 对话历史
   * @param {string} memorySummary - 记忆摘要
   * @returns {string} 完整的系统提示词
   */
  buildSystemPromptWithMemory(world, session = null, settings = null, conversationHistory = [], memorySummary = '') {
    const parts = []

    // 1. 世界观前置文本
    if (world.preText) {
      parts.push(world.preText)
      parts.push('\n---\n')
    }

    // 2. 记忆摘要（如果有）
    if (memorySummary) {
      parts.push('# 对话记忆摘要')
      parts.push(memorySummary)
      parts.push('\n---\n')
    }

    // 3. 知识库上下文（根据当前对话提取相关条目）
    if (world.worldBook && world.worldBook.length > 0) {
      const worldBookContext = this.buildWorldBookContext(world.worldBook, session)
      if (worldBookContext) {
        parts.push('# 知识库')
        parts.push(worldBookContext)
        parts.push('\n---\n')
      }
    }

    // 4. 核心AI指令集（完整保留，包括NSFW内容）
    if (world.postText) {
      let postText = world.postText
      
      // 如果设置了NSFW模式，确保NSFW内容被启用
      if (settings && settings.nsfwEnabled) {
        // 如果postText中没有NSFW相关指令，添加一个
        if (!postText.toLowerCase().includes('nsfw') && !postText.toLowerCase().includes('成人')) {
          postText += '\n\n[NSFW模式已启用：允许生成成人向内容]'
        }
      } else {
        // 如果NSFW未启用，移除或注释NSFW相关指令
        postText = postText.replace(/\[NSFW[^\]]*\]/gi, '[NSFW模式未启用]')
      }
      
      parts.push(postText)
      parts.push('\n---\n')
    }

    // 5. 输出格式规范
    if (world.prePrompt) {
      parts.push(world.prePrompt)
    }

    // 6. 记忆和连贯性要求
    parts.push('\n---\n')
    parts.push('# 重要提醒')
    parts.push('1. 必须严格遵循上述世界观设定和规则')
    parts.push('2. 必须参考对话记忆摘要，保持情节连贯性')
    parts.push('3. 必须记住玩家角色的重要决定和行动')
    parts.push('4. 必须保持角色行为的一致性')
    parts.push('5. 必须确保世界状态变化的连续性')

    return parts.join('\n')
  }

  /**
   * 更新会话记忆
   * @param {object} session - 会话对象
   * @param {object} parsedResponse - 解析后的响应
   * @returns {object} 更新后的会话
   */
  updateSessionMemory(session, parsedResponse) {
    if (!session) return session

    const updatedSession = { ...session }

    // 更新记忆
    if (parsedResponse.memory && parsedResponse.memory.length > 0) {
      updatedSession.memory = [
        ...(updatedSession.memory || []),
        ...parsedResponse.memory
      ].slice(-50) // 保留最近50条记忆
    }

    // 更新角色信息
    if (parsedResponse.characterInfo) {
      updatedSession.characterInfo = {
        ...(updatedSession.characterInfo || {}),
        ...parsedResponse.characterInfo
      }
    }

    // 更新世界信息
    if (parsedResponse.worldInfo) {
      updatedSession.worldInfo = {
        ...(updatedSession.worldInfo || {}),
        ...parsedResponse.worldInfo
      }
    }

    return updatedSession
  }
}

// 创建单例
const worldAIService = new WorldAIService()

export default worldAIService

